import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Pause, Play, Volume2, X } from 'lucide-react'
import { usePlayerStore, type PlayerTrack } from '@/stores/player'
import { cn } from '@/lib/cn'

/**
 * Story E03.05 — floating mini-player.
 *
 * Mounted once at the application root (`__root.tsx`). Reads the
 * current track from `usePlayerStore` and renders a fixed
 * bottom-right card with play/pause, seek bar, volume, and close.
 *
 * The body is keyed on `track.id` so a new track remounts the inner
 * component with fresh transient state (currentTime, duration,
 * isPlaying) — no resync `useEffect` (cascading-renders lint).
 *
 * Audio cookie story: the `<audio>` `src` points at same-origin API
 * endpoints (e.g. `/api/voices/:id/sample`) so the auth cookie flows
 * automatically — no fetch+blob plumbing.
 */
export function MiniPlayer() {
  const track = usePlayerStore((s) => s.currentTrack)
  if (!track) return null
  return <PlayerBody key={track.id} track={track} />
}

interface BodyProps {
  track: PlayerTrack
}

function PlayerBody({ track }: BodyProps) {
  const { t } = useTranslation()
  const volume = usePlayerStore((s) => s.volume)
  const loadError = usePlayerStore((s) => s.loadError)
  const setVolume = usePlayerStore((s) => s.setVolume)
  const setLoadError = usePlayerStore((s) => s.setLoadError)
  const stop = usePlayerStore((s) => s.stop)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)

  // Push volume changes into the live element. Reads volume from store,
  // not from local state, so siblings can scrub it without remounting.
  useEffect(() => {
    if (!audioRef.current) return
    audioRef.current.volume = volume
  }, [volume])

  const togglePlay = () => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      void el.play().catch(() => setLoadError(true))
    } else {
      el.pause()
    }
  }

  const onSeekChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current
    if (!el || !Number.isFinite(duration) || duration <= 0) return
    const next = Number(e.target.value)
    el.currentTime = next
    setCurrentTime(next)
  }

  const onVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(e.target.value))
  }

  return (
    <div
      role="region"
      aria-label={t('player.now_playing')}
      className={cn(
        'fixed bottom-4 right-4 z-[700] w-[360px] max-w-[calc(100vw-2rem)]',
        'bg-bg-card border border-border rounded-md shadow-md',
        'p-3 flex flex-col gap-2',
      )}
    >
      <audio
        ref={audioRef}
        src={track.src}
        preload="auto"
        autoPlay
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration
          setDuration(Number.isFinite(d) ? d : 0)
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => {
          setIsPlaying(true)
          setLoadError(false)
        }}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
        onError={() => setLoadError(true)}
      />

      <div className="flex items-start gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-medium text-text truncate"
            title={track.title}
          >
            {track.title}
          </div>
          {track.subtitle && (
            <div
              className="text-xs text-text-secondary truncate"
              title={track.subtitle}
            >
              {track.subtitle}
            </div>
          )}
          {loadError && (
            <div role="alert" className="text-xs text-red mt-1">
              {t('player.load_error')}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={stop}
          aria-label={t('player.close')}
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-sm shrink-0',
            'text-text-secondary hover:bg-bg-hover hover:text-text',
            'focus:bg-bg-hover focus:outline-none transition-colors',
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? t('player.pause') : t('player.play')}
          className={cn(
            'inline-flex items-center justify-center h-8 w-8 rounded-full shrink-0',
            'bg-blue text-white hover:bg-blue-hover transition-colors',
          )}
        >
          {isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>
        <input
          type="range"
          min={0}
          max={duration > 0 ? duration : 0}
          step={0.1}
          value={currentTime}
          onChange={onSeekChange}
          aria-label={t('player.seek')}
          disabled={duration === 0}
          className="flex-1 accent-blue"
        />
        <span className="text-[11px] text-text-muted tabular-nums shrink-0">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Volume2
          className="h-4 w-4 text-text-secondary shrink-0"
          aria-hidden
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          onChange={onVolumeChange}
          aria-label={t('player.volume')}
          className="flex-1 accent-blue"
        />
      </div>
    </div>
  )
}

/**
 * Format seconds as `m:ss`. `0` and non-finite values render as `0:00`.
 * Pure helper kept inline because nothing else in the codebase needs
 * this format yet — promote it if a second caller appears.
 */
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
